import Vue from "vue";
import { ConfigClient } from "@/api/LumediPublicClient";
import { i18n } from "@/plugins/i18n";

/**
 * One deployment serves several hostnames (e.g. patient.mwmcc.ca and patient.launchithealth.ca).
 * Which brand to render is decided by the server from the hostname the page was loaded from
 * (GET /api/config/branding, driven by the Branding:LaunchitHealthHostnames app setting) so
 * staging/production hostnames are configuration, not build-time constants.
 */
export type Brand = "mwm" | "launchithealth";

/** Reactive so AppLogo/App.vue re-render once loadBranding() resolves. Defaults to MWM. */
export const branding = Vue.observable({ brand: "mwm" as Brand });

const LAUNCHIT_HEALTH_TITLE = "Launchit Health";

// Launchit Health wording overrides, one file per locale. Kept OUTSIDE src/locales on purpose:
// plugins/i18n.ts loads every json under src/locales keyed by file name, so a nested en.json there
// would replace the default English messages instead of overriding them for this brand only.
const launchitHealthMessages = (() => {
  const files = require.context("../locales-brand/launchithealth", false, /\.json$/);
  const result: Record<string, any> = {};
  files.keys().forEach((key) => {
    const locale = key.replace(/^\.\//, "").replace(/\.json$/, "");
    result[locale] = files(key);
  });
  return result;
})();

const applyLaunchitHealthMessages = () => {
  const target = (i18n as any).global ?? i18n;
  Object.keys(launchitHealthMessages).forEach((locale) => {
    target.mergeLocaleMessage(locale, launchitHealthMessages[locale]);
  });
};

/**
 * Fetches the brand for the current hostname and applies it. Fire-and-forget at app boot
 * (see main.ts); best-effort - a failed fetch simply leaves the default MWM branding.
 */
let brandingLoaded: Promise<void> | null = null;

/**
 * Resolves once the brand for this hostname is known (or the lookup failed and MWM stays the default).
 * Use before anything that must not happen on the Launchit Health site, e.g. the MWM app deep link.
 */
export const whenBrandingLoaded = (): Promise<void> => brandingLoaded ?? loadBranding();

export const loadBranding = (): Promise<void> => {
  if (!brandingLoaded) brandingLoaded = fetchBranding();
  return brandingLoaded;
};

const fetchBranding = async (): Promise<void> => {
  try {
    // Must hit the host the page was loaded from (not VUE_APP_PATIENT_API_URL, which can point at a
    // different hostname, e.g. *.azurewebsites.net) since the brand is decided from the request host.
    const response = await new ConfigClient(window.location.origin).getBranding();
    branding.brand = response.brand === "launchithealth" ? "launchithealth" : "mwm";
  } catch (e) {
    console.warn("Failed to load branding", e);
    return;
  }

  if (branding.brand === "launchithealth") {
    applyLaunchitHealthMessages();
    document.title = LAUNCHIT_HEALTH_TITLE;
  }
};
